import { DownOutlined, GlobalOutlined } from '@ant-design/icons'
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api'
import { useEffect, useMemo, useState } from 'react'
import { GOOGLE_MAPS_LIBRARIES } from '../../../utils/google-maps'
import { PageLang } from '../pageLang'

const ICON_CIRCLE_BG = '#E6F3FF'

const MAP_PIN_COLOR = '#77FF61'

const SINGLE_DOT_SIZE = 10
const SINGLE_DOT_SIZE_ACTIVE = 16

const ROTATION_INTERVAL_MS = 3500

// 95% of bookings come from Bangkok, so the map focuses on the metro area
// instead of showing the whole country. Zoomed out enough to show the whole
// Bangkok area by default.
const BANGKOK_CENTER = { lat: 13.7563, lng: 100.5018 }
const BANGKOK_ZOOM = 10

// Light theme matching the rest of the app's white cards, with the brand's
// gold/orange administrative borders (#D9A15B) standing in for the old
// static map's province outlines.
const LIGHT_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#F3F4F6' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#F3F4F6' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#667085' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#E4E7EC' }] },
  {
    featureType: 'administrative.province',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#D9A15B' }, { weight: 1 }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#344054' }],
  },
  // Hide every point-of-interest label/icon (offices, businesses, attractions, etc.) —
  // a clean street layout is all this decorative map needs.
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#FFFFFF' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#E4E7EC' }] },
  // Hide highway/route-number shield icons (e.g. "338", "AH2") — street names stay.
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#E9ECF1' }] },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#667085' }],
  },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#D9E6F5' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7A93AD' }] },
]

// Decorative-only positions (real Bangkok station coordinates) shown while
// the first fetch is still in flight.
const SKELETON_PIN_POSITIONS = [
  { lat: 13.7562386, lng: 100.5332286 },
  { lat: 13.722368, lng: 100.747809 },
  { lat: 13.7668421, lng: 100.5855015 },
  { lat: 13.912512828190035, lng: 100.59622685090105 },
  { lat: 13.6842897, lng: 100.5387858 },
]

interface WeeklyBooking {
  lat: number
  lng: number
  vehicle: string
  location: Record<PageLang, string>
  price: string
}

interface ApiWeeklyBooking {
  reservationNo: number
  vehicleBrand: string
  vehicleModel: string
  pricePerDay: number
  city: string | null
  latitude: number | null
  longitude: number | null
  bookedAt: string
}

const WEEKLY_BOOKINGS_API_URL = `${process.env.REACT_APP_TS_RESTFUL_API_URL}/submission/v2/weekly-bookings-map/`
const WEEKLY_BOOKINGS_REFRESH_MS = 60000

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const mapApiBookingToWeeklyBooking = (item: ApiWeeklyBooking): WeeklyBooking | null => {
  if (!item.latitude || !item.longitude || !item.city || !item.pricePerDay) {
    // Skip bookings we can't place on the map or that have no per-day rate
    // (e.g. shuttle/per-ride vehicles like MUVMI don't use dayrate).
    return null
  }
  const vehicle = `${toTitleCase(item.vehicleBrand)} ${item.vehicleModel.replace(/_/g, ' ')}`.trim()
  const price = `฿${Math.round(item.pricePerDay).toLocaleString('en-US')}`
  return {
    lat: item.latitude,
    lng: item.longitude,
    vehicle,
    location: { EN: item.city, TH: item.city },
    price,
  }
}

interface MapPinGroup {
  lat: number
  lng: number
  bookingIndices: number[]
}

// Bookings that share the exact same station are drawn as one dot with a small
// count badge (e.g. "3") instead of overlapping/hiding each other — every
// other location still gets its own plain individual dot.
const buildPinGroups = (bookings: WeeklyBooking[]): MapPinGroup[] => {
  const groups = new Map<string, MapPinGroup>()
  bookings.forEach((booking, index) => {
    const key = `${booking.lat.toFixed(4)},${booking.lng.toFixed(4)}`
    const group = groups.get(key)
    if (group) {
      group.bookingIndices.push(index)
    } else {
      groups.set(key, { lat: booking.lat, lng: booking.lng, bookingIndices: [index] })
    }
  })
  return Array.from(groups.values())
}

const ICONS = {
  forOwners:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/29rRVpdDUrj8n7K9mCtwBhbvuFF2iUpUGZn5jwHqdMqDHc798JSZgb1d8xtuDmaykHmMLbuHKXntKpe6bGt29t33.png',
  avgIncome:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/28srQjS18EorYiVhCVYXckfcZQrCVtbfXYu5WAVv7VtL1qQUoV54GQ21dqg2Qga9s5aoEwgHhoMGzKWFLPSawNnT.png',
  alsoListedOn:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/5b2MY8SfVg9Do6dwUpdgRmnfuGLkWLGWykqUmBbDYpAqXgkujvnFVa1XNGZvCnn5MUSVD8Xb4ZSQEKYCCZpvgGD8.png',
  vehicleSafety:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/67mRWB4uSVajbKVLoUg792jKrCvhqQfasrJTeGYnvux7xkc3AGFFiVgfg1fZH4ged7ib1ZB2HG8AX5SxgJBSQRx1.png',
  verifiedRenters:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/2NMMzb4RUuXPH3Cc3N6xmBQQvKHyA4imno3nYXPq1yHx3w6y9obFMTbCvKomppUrEwX1reTnMFKx5N5vs89y2Dy9.png',
  insurance1:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/4YLErD1e9jmTq9JAD1xa9vX69kVKYhp3Kz5QXgXbbzc2oNmwtBzVpmbg59tA7VjdWd6MLzYrYf5RSY4zKoaK7whP.png',
  insurance2:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/3YmVhXfMcxGLT257WE8r6DJNhmTWihUi7CtbUfeidS4MUCMeQTWQB1fHdmkrRhaHcvWvZQy123B6rrvce6CTW7qb.png',
  amazingThailand1:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/3rJk4v2BT3uc6ugBwSDf4n6468S4RmpYHhced2Yhfn6E871c1bY8VEiHjL8kKivBVgTJHH7mhVoq6U2sPUziAC6V.png',
  amazingThailand2:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/3yPL5g6VW3G2UJehSUzSpeTUHKu6W6K7QjZBLXQvVKBfn8pHyKAeaXSsQdky8rTSuM62RNxpB2Ub7jZLvNH1zszm.png',
  tripCom:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/2uNu69zF7mS9MuCpq4vUQqPB3oogLMgikNWuZLFdZmwTMSmquY9N1NXrJgiqMC4qgT92FGNzszLkrjqTZkGFdnva.png',
  klook:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/5jvWnLVRuvS4G8fqV1NPv7g5G293csadknh64RGFpuZpePvhgM85Lu6LABZAATkDHGrsr31QGHH3G7w8RP25uweK.png',
}

const HERO_BACKGROUND_IMAGE =
  'https://ts-upload.sgp1.digitaloceanspaces.com/cms/3qaoaSX9Wis2oh927cpfGGmnBr6vAf337gSRgyFGsY56KrnHcPoTz1JunTWbMPAP9dsDB9MXoxQQAizD86nPX6ig.png'

const LINE_ICON =
  'https://ts-upload.sgp1.digitaloceanspaces.com/cms/1KyowfnwDN1QbUQhtJPrCRnqquAUHwRFhavCJLGn7hqECYJRi2oT3VQAqfyvbfs7E2kRAGoC5PfxG4PnxcxPRQGe.png'

const CONTENT: Record<
  PageLang,
  {
    forOwners: string
    headline: string
    subtext: string
    lineBtn: string
    otherBtn: string
    avgIncomeTitle: string
    avgIncomeLines: [string, string]
    alsoListedOnTitle: string
    vehicleSafetyTitle: string
    vehicleSafetyLine: string
    verifiedRentersTitle: string
    verifiedRentersLine: string
    insuranceLabel: string
    amazingThailandLabel: string
    officialPartner: string
    weeklyBookingsLive: string
    weeklyBookingsLabel: string
    newBookingLabel: string
    newBookingPerDay: string
  }
> = {
  EN: {
    forOwners: 'For vehicle owners',
    headline: 'The most trusted car-sharing platform for owners',
    subtext: 'List your car and let HAUP handle bookings, renters and support.',
    lineBtn: 'Log in or register with LINE',
    otherBtn: 'Login with others',
    avgIncomeTitle: 'Average income',
    avgIncomeLines: ['Earn THB 12,000 ~', 'THB 15,000 per month'],
    alsoListedOnTitle: 'Also listed on',
    vehicleSafetyTitle: 'Vehicle safety',
    vehicleSafetyLine: '24/7 GPS & real-time car tracking',
    verifiedRentersTitle: 'Verified renters',
    verifiedRentersLine: '100% identity checked',
    insuranceLabel: 'Insurance',
    amazingThailandLabel: 'Amazing Thailand',
    officialPartner: 'Official Partner',
    weeklyBookingsLive: 'LIVE',
    weeklyBookingsLabel: 'Weekly bookings',
    newBookingLabel: 'New booking',
    newBookingPerDay: 'per day',
  },
  TH: {
    forOwners: 'สำหรับเจ้าของรถ',
    headline: 'แพลตฟอร์มแบ่งปันรถที่เจ้าของรถไว้วางใจมากที่สุด',
    subtext: 'ลงทะเบียนรถของคุณ แล้วให้ HAUP ดูแลการจอง ผู้เช่า และการซัพพอร์ตให้',
    lineBtn: 'เข้าสู่ระบบหรือสมัครด้วย LINE',
    otherBtn: 'เข้าสู่ระบบด้วยวิธีอื่น',
    avgIncomeTitle: 'รายได้เฉลี่ย',
    avgIncomeLines: ['รายได้ 12,000 ~', '15,000 บาทต่อเดือน'],
    alsoListedOnTitle: 'ลงประกาศเพิ่มเติมบน',
    vehicleSafetyTitle: 'ความปลอดภัยของรถ',
    vehicleSafetyLine: 'ติดตามรถแบบเรียลไทม์ด้วย GPS ตลอด 24 ชม.',
    verifiedRentersTitle: 'ผู้เช่าที่ผ่านการยืนยันตัวตน',
    verifiedRentersLine: 'ยืนยันตัวตน 100%',
    insuranceLabel: 'ประกันภัย',
    amazingThailandLabel: 'Amazing Thailand',
    officialPartner: 'พันธมิตรอย่างเป็นทางการ',
    weeklyBookingsLive: 'LIVE',
    weeklyBookingsLabel: 'การจองรายสัปดาห์',
    newBookingLabel: 'การจองใหม่',
    newBookingPerDay: 'ต่อวัน',
  },
}

const IconCircle = ({ src, size = 40 }: { src: string; size?: number }) => (
  <div
    style={{
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '50%',
      backgroundColor: ICON_CIRCLE_BG,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '10px',
    }}
  >
    <img src={src} alt="" style={{ width: `${size * 0.5}px`, height: `${size * 0.5}px` }} />
  </div>
)

const PartnerBadge = ({
  icon1,
  icon2,
  title,
  officialPartner,
}: {
  icon1: string
  icon2: string
  title: string
  officialPartner: string
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <img src={icon1} alt="" style={{ width: '22px', height: '22px', borderRadius: '50%' }} />
      <img src={icon2} alt="" style={{ width: '22px', height: '22px', borderRadius: '50%' }} />
    </div>
    <div>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#212427' }}>{title}</div>
      <div style={{ fontSize: '11px', color: '#8C97A7' }}>{officialPartner}</div>
    </div>
  </div>
)

const StatCard = ({
  icon,
  title,
  lines,
  footer,
}: {
  icon: string
  title: string
  lines: React.ReactNode
  footer?: React.ReactNode
}) => (
  <div
    style={{
      backgroundColor: '#fff',
      border: '1px solid #E4E7EC',
      borderRadius: '12px',
      padding: '14px',
      boxShadow: '0px 5px 10px 0px #0000001A',
    }}
  >
    <IconCircle src={icon} />
    <div style={{ fontSize: '13px', fontWeight: 600, color: '#212427', marginBottom: '2px' }}>
      {title}
    </div>
    <div style={{ fontSize: '12px', color: '#667085', lineHeight: '1.4' }}>{lines}</div>
    {footer && (
      <>
        <div style={{ borderTop: '1px solid #E4E7EC', margin: '10px 0' }} />
        {footer}
      </>
    )}
  </div>
)

const WeeklyBookingsMap = ({
  lang,
  liveLabel,
  weeklyBookingsLabel,
  newBookingLabel,
  perDay,
}: {
  lang: PageLang
  liveLabel: string
  weeklyBookingsLabel: string
  newBookingLabel: string
  perDay: string
}) => {
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  })
  const [bookings, setBookings] = useState<WeeklyBooking[]>([])
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    let cancelled = false

    const fetchBookings = async () => {
      try {
        const response = await fetch(WEEKLY_BOOKINGS_API_URL)
        if (!response.ok) throw new Error('Network response was not ok')
        const json = await response.json()
        if (!json.result || !Array.isArray(json.data)) {
          throw new Error(json.message || 'Failed to fetch weekly bookings')
        }
        const mapped = (json.data as ApiWeeklyBooking[])
          .map(mapApiBookingToWeeklyBooking)
          .filter((booking): booking is WeeklyBooking => booking !== null)
        if (!cancelled && mapped.length > 0) {
          setBookings(mapped)
          setActiveIndex(0)
        }
      } catch (error) {
        // Keep whatever was last shown rather than break the login page over this.
        console.error('Failed to load weekly bookings', error)
      }
    }

    fetchBookings()
    const refreshId = setInterval(fetchBookings, WEEKLY_BOOKINGS_REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(refreshId)
    }
  }, [])

  useEffect(() => {
    if (bookings.length < 2) return
    const id = setInterval(() => {
      setActiveIndex((current) => {
        // Exclude the current booking so the same dot never fires twice in a row.
        const next = Math.floor(Math.random() * (bookings.length - 1))
        return next >= current ? next + 1 : next
      })
    }, ROTATION_INTERVAL_MS)
    return () => clearInterval(id)
  }, [bookings])

  const activeBooking = bookings[activeIndex]
  const isLoading = bookings.length === 0
  const pinGroups = useMemo(() => buildPinGroups(bookings), [bookings])

  return (
    <div style={{ position: 'relative', marginTop: '-150px', padding: '0 16px 8px' }}>
      <style>{`
        @keyframes hbWeeklyPinPing {
          0% { transform: translate(-50%, -50%) scale(0.7); opacity: 0.6; }
          70% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
          100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
        }
        @keyframes hbWeeklyPinBreathe {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          50% { transform: translate(-50%, -50%) scale(1.25); opacity: 0.85; }
        }
        @keyframes hbWeeklyCardFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hbWeeklySkeletonPulse {
          0%, 100% { opacity: 0.25; }
          50% { opacity: 0.55; }
        }
        @keyframes hbWeeklySkeletonShimmer {
          0% { background-position: -120px 0; }
          100% { background-position: 120px 0; }
        }
      `}</style>
      <div
        style={{
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          height: '410px',
          backgroundColor: '#F3F4F6',
          boxShadow: '0px 5px 10px 0px #0000001A',
        }}
      >
        {isMapLoaded && window.google?.maps && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={BANGKOK_CENTER}
            zoom={BANGKOK_ZOOM}
            options={{
              styles: LIGHT_MAP_STYLE,
              disableDefaultUI: true,
              zoomControl: false,
              // Single-finger touch scrolls the page; two fingers pan/zoom the map.
              gestureHandling: 'cooperative',
              clickableIcons: false,
              backgroundColor: '#F3F4F6',
            }}
          >
            {isLoading
              ? SKELETON_PIN_POSITIONS.map((pos, index) => (
                  <OverlayView
                    key={index}
                    position={pos}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(102, 112, 133, 0.4)',
                        transform: 'translate(-50%, -50%)',
                        animation: `hbWeeklySkeletonPulse 1.6s ease-in-out infinite ${
                          index * 0.15
                        }s`,
                      }}
                    />
                  </OverlayView>
                ))
              : // One dot per unique station; bookings sharing a station collapse into
                // one dot with a count badge instead of overlapping each other.
                pinGroups.map((group, groupIndex) => {
                  const isActive = group.bookingIndices.includes(activeIndex)
                  const count = group.bookingIndices.length
                  const isCluster = count > 1
                  const dotSize =
                    (isActive ? SINGLE_DOT_SIZE_ACTIVE : SINGLE_DOT_SIZE) + (isCluster ? 4 : 0)

                  return (
                    <OverlayView
                      key={groupIndex}
                      position={{ lat: group.lat, lng: group.lng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div style={{ position: 'relative' }}>
                        {isActive && (
                          <span
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              backgroundColor: MAP_PIN_COLOR,
                              transform: 'translate(-50%, -50%)',
                              animation: 'hbWeeklyPinPing 1.8s ease-out infinite',
                            }}
                          />
                        )}
                        <span
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: `${dotSize}px`,
                            height: `${dotSize}px`,
                            borderRadius: '50%',
                            backgroundColor: MAP_PIN_COLOR,
                            border: '2px solid rgba(255, 255, 255, 0.95)',
                            opacity: isActive ? 1 : 0.5,
                            // A thin dark ring keeps the bright pin readable against light map tiles.
                            boxShadow: isActive
                              ? `0 0 0 1px rgba(33, 36, 39, 0.35), 0 0 12px 2px ${MAP_PIN_COLOR}`
                              : '0 0 0 1px rgba(33, 36, 39, 0.35)',
                            transform: 'translate(-50%, -50%)',
                            // Resting dots get a gentle "alive" breathing pulse, staggered per
                            // pin; the active one instead gets the stronger ping ring above.
                            animation: isActive
                              ? undefined
                              : `hbWeeklyPinBreathe 2.4s ease-in-out infinite ${
                                  (groupIndex % 5) * 0.3
                                }s`,
                            transition:
                              'width 0.4s ease, height 0.4s ease, box-shadow 0.4s ease, opacity 0.4s ease',
                          }}
                        />
                        {isCluster && (
                          <span
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              transform: `translate(${dotSize / 2 - 2}px, -${dotSize / 2 + 2}px)`,
                              backgroundColor: '#fff',
                              color: '#212427',
                              fontSize: '9px',
                              fontWeight: 700,
                              borderRadius: '8px',
                              padding: '1px 5px',
                              boxShadow: '0px 2px 4px 0px #00000040',
                            }}
                          >
                            {count}
                          </span>
                        )}
                      </div>
                    </OverlayView>
                  )
                })}
          </GoogleMap>
        )}

        <div
          style={{
            position: 'absolute',
            top: '14px',
            left: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '6px 11px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#212427',
            boxShadow: '0px 4px 10px 0px #00000026',
          }}
        >
          <span
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              backgroundColor: '#FF3B30',
              flexShrink: 0,
            }}
          />
          {liveLabel} &bull; {weeklyBookingsLabel}
        </div>

        {isLoading && (
          <div
            style={{
              position: 'absolute',
              right: '6px',
              // Cleared enough to not cover Google's required attribution footer.
              bottom: '32px',
              width: '150px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '11px 13px',
              boxShadow: '0px 5px 10px 0px #0000001A',
            }}
          >
            {[
              ['40%', '10px'],
              ['80%', '15px'],
              ['60%', '13px'],
            ].map(([width, height], index) => (
              <div
                key={index}
                style={{
                  width,
                  height,
                  marginTop: index === 0 ? 0 : '8px',
                  borderRadius: '4px',
                  background: 'linear-gradient(90deg, #EEF0F3 25%, #F6F7F9 37%, #EEF0F3 63%)',
                  backgroundSize: '240px 100%',
                  animation: 'hbWeeklySkeletonShimmer 1.4s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        )}

        {!isLoading && activeBooking && (
          <div
            key={activeIndex}
            style={{
              position: 'absolute',
              right: '6px',
              // Cleared enough to not cover Google's required attribution footer.
              bottom: '32px',
              minWidth: '190px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '11px 13px',
              boxShadow: '0px 5px 10px 0px #0000001A',
              animation: 'hbWeeklyCardFadeIn 0.4s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: '#29A016',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#29A016' }}>
                {newBookingLabel}
              </span>
            </div>
            <div
              style={{ fontSize: '15px', fontWeight: 600, color: '#212427', marginBottom: '6px' }}
            >
              {activeBooking.vehicle}
            </div>
            <div
              style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}
            >
              <span style={{ fontSize: '12px', color: '#667085' }}>
                {activeBooking.location[lang]}
              </span>
              <span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#29A016' }}>
                  {activeBooking.price}
                </span>{' '}
                <span style={{ fontSize: '11px', color: '#212427' }}>{perDay}</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export interface HeroSectionProps {
  lang: PageLang
  onChangeLang: (lang: PageLang) => void
  onLineLogin: () => void
  onOtherLogin: () => void
}

export const HeroSection = ({
  lang,
  onChangeLang,
  onLineLogin,
  onOtherLogin,
}: HeroSectionProps) => {
  const t = CONTENT[lang]
  const [langMenuOpen, setLangMenuOpen] = useState(false)

  return (
    <div>
      <div
        style={{
          position: 'relative',
          backgroundImage: `url(${HERO_BACKGROUND_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 35%',
          padding: '16px 16px 180px',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.05) 100%)',
          }}
        />
        <div style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#fff',
                color: '#0048B3',
                fontSize: '12px',
                fontWeight: 600,
                padding: '6px 14px',
                borderRadius: '16px',
              }}
            >
              <img src={ICONS.forOwners} alt="" style={{ width: '14px', height: '14px' }} />
              {t.forOwners}
            </div>

            <div style={{ position: 'relative' }}>
              <div
                onClick={() => setLangMenuOpen((open) => !open)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <GlobalOutlined style={{ fontSize: '14px' }} />
                {lang}
                <DownOutlined style={{ fontSize: '9px' }} />
              </div>

              {langMenuOpen && (
                <>
                  <div
                    onClick={() => setLangMenuOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 9 }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      right: 0,
                      backgroundColor: '#fff',
                      borderRadius: '10px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                      overflow: 'hidden',
                      zIndex: 10,
                      minWidth: '110px',
                    }}
                  >
                    {(['TH', 'EN'] as PageLang[]).map((option) => (
                      <div
                        key={option}
                        onClick={() => {
                          onChangeLang(option)
                          setLangMenuOpen(false)
                        }}
                        style={{
                          padding: '10px 14px',
                          fontSize: '13px',
                          fontWeight: lang === option ? 700 : 500,
                          color: lang === option ? '#0048B3' : '#212427',
                          backgroundColor: lang === option ? '#F5F9FF' : '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        {option === 'TH' ? 'ไทย' : 'English'}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#fff', margin: '0 0 8px' }}>
            {t.headline}
          </h1>
          <p
            style={{ fontSize: '14px', color: '#F0F1F4', lineHeight: '1.5', marginBottom: '24px' }}
          >
            {t.subtext}
          </p>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '4px' }}>
            <button
              onClick={onLineLogin}
              style={{
                flex: 1.6,
                height: '48px',
                backgroundColor: '#06C755',
                border: 'none',
                borderRadius: '24px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(6, 199, 85, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <img src={LINE_ICON} alt="" style={{ width: '20px', height: '20px' }} />
              {t.lineBtn}
            </button>
            <button
              onClick={onOtherLogin}
              style={{
                flex: 1,
                height: '48px',
                whiteSpace: 'nowrap',
                backgroundColor: 'transparent',
                border: '1.5px solid #fff',
                borderRadius: '24px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {t.otherBtn}
            </button>
          </div>
        </div>
      </div>

      <WeeklyBookingsMap
        lang={lang}
        liveLabel={t.weeklyBookingsLive}
        weeklyBookingsLabel={t.weeklyBookingsLabel}
        newBookingLabel={t.newBookingLabel}
        perDay={t.newBookingPerDay}
      />

      <div
        style={{
          position: 'relative',
          marginTop: '8px',
          padding: '0 16px 8px',
          display: 'flex',
          gap: '10px',
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <StatCard
            icon={ICONS.avgIncome}
            title={t.avgIncomeTitle}
            lines={
              <>
                {t.avgIncomeLines[0]} <br />
                {t.avgIncomeLines[1]}
              </>
            }
          />
          <StatCard
            icon={ICONS.vehicleSafety}
            title={t.vehicleSafetyTitle}
            lines={t.vehicleSafetyLine}
            footer={
              <PartnerBadge
                icon1={ICONS.insurance1}
                icon2={ICONS.insurance2}
                title={t.insuranceLabel}
                officialPartner={t.officialPartner}
              />
            }
          />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <StatCard
            icon={ICONS.alsoListedOn}
            title={t.alsoListedOnTitle}
            lines={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                <img src={ICONS.tripCom} alt="Trip.com" style={{ height: '16px' }} />
                <img src={ICONS.klook} alt="Klook" style={{ height: '16px' }} />
              </div>
            }
          />
          <StatCard
            icon={ICONS.verifiedRenters}
            title={t.verifiedRentersTitle}
            lines={t.verifiedRentersLine}
          />
          <div
            style={{
              backgroundColor: '#fff',
              border: '1px solid #E4E7EC',
              borderRadius: '12px',
              padding: '14px',
              boxShadow: '0px 5px 10px 0px #0000001A',
            }}
          >
            <PartnerBadge
              icon1={ICONS.amazingThailand1}
              icon2={ICONS.amazingThailand2}
              title={t.amazingThailandLabel}
              officialPartner={t.officialPartner}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeroSection
